import * as React from 'react';
import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../../../components/inputs/LanguageSelector';
import { OnboardingContainer } from '../../../components/displays/OnboardingContainer';
import { OnboardingCard } from '../../../components/displays/OnboardingCard';
import { Button, CircularProgress } from '@mui/material';
import PasswordField from '../../../components/inputs/PasswordField/PasswordField';

export default function CreateAccount(props: { onSignIn?: () => void; email: string; createAccount?: (name: string, password: string) => Promise<void> }) {
    const { t, i18n } = useTranslation();
    const [passwordError, setPasswordError] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
    const [creatingAccount, setCreatingAccount] = React.useState(false);

    const validateInputs = () => {
        const password = document.getElementById('password') as HTMLInputElement;

        let isValid = true;

        if (!password.value || password.value.length < 8) {
            setPasswordError(true);
            setPasswordErrorMessage(t('common.form.invalidPassword'));
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        return isValid;
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (passwordError) {
            return;
        }

        const data = new FormData(event.currentTarget);
        const name = data.get('name') as string;
        const password = data.get('password') as string;

        if (props.createAccount) {
            setCreatingAccount(true);
            await props.createAccount(name, password).finally(() => {
                setCreatingAccount(false);
            });
        }
    };

    return (
        <OnboardingContainer direction='column' justifyContent='space-between'>
            <OnboardingCard variant='outlined' style={{ position: 'relative' }}>
                <Stack direction='row' sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography component='h1' variant='h4' sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}>
                        {t('onboarding.createAccount')}
                    </Typography>
                    <LanguageSelector onChange={(locale) => i18n.changeLanguage(locale)} />
                </Stack>
                <Box
                    component='form'
                    onSubmit={handleSubmit}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <FormControl>
                        <FormLabel htmlFor='name'>{t('common.fullName')}</FormLabel>
                        <TextField autoComplete='name' name='name' fullWidth id='name' placeholder='John Doe' />
                    </FormControl>
                    <FormControl>
                        <FormLabel htmlFor='email'>{t('common.form.email')}</FormLabel>
                        <TextField required fullWidth id='email' placeholder='your@email.com' name='email' autoComplete='email' variant='outlined' color={'primary'} value={props.email} disabled />
                    </FormControl>
                    <FormControl>
                        <FormLabel htmlFor='password'>{t('common.form.password')}</FormLabel>
                        <PasswordField required fullWidth error={passwordError} helperText={passwordErrorMessage} label={undefined} color={passwordError ? 'error' : 'primary'} />
                    </FormControl>
                    <Button type='submit' fullWidth variant='contained' onClick={validateInputs} sx={{ marginTop: 2 }} loading={creatingAccount} loadingIndicator={<CircularProgress size={16} />}>
                        {t('onboarding.createAccount')}
                    </Button>
                    <Typography sx={{ textAlign: 'center' }}>
                        {t('welcome.signUp.alreadyHaveAccount')}{' '}
                        <Link component='button' onClick={props.onSignIn} variant='body2' sx={{ verticalAlign: 'baseline' }}>
                            {t('common.signIn')}
                        </Link>
                    </Typography>
                </Box>
            </OnboardingCard>
        </OnboardingContainer>
    );
}
